import React, { useState, useRef, useEffect } from 'react';
import { X, FileText } from 'lucide-react';
import { useMinimizedFiles } from '../../hooks/useMinimizedFiles';
import { DriveFile } from '../../lib/services/googleDriveService';

interface GlobalMinimizedFilesProps {
  onMaximizeFile: (file: DriveFile) => void;
}

export function GlobalMinimizedFiles({ onMaximizeFile }: GlobalMinimizedFilesProps) {
  const { minimizedFiles, removeMinimizedFile } = useMinimizedFiles();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const hasFiles = minimizedFiles.length > 0;

  // Non mostrare nulla se non ci sono file
  if (!hasFiles) {
    return null;
  }

  const handleClose = (fileId: string, fileName: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    const confirmed = window.confirm(`Sei sicuro di voler chiudere "${fileName}"?`);
    if (confirmed) {
      removeMinimizedFile(fileId);
    }
  };

  const getFileName = (name: string) => {
    return name.length > 25 ? `${name.substring(0, 22)}...` : name;
  };

  const getFileTypeName = (mimeType: string | undefined) => {
    if (!mimeType) return 'FILE';
    
    const mimeTypeLower = mimeType.toLowerCase();
    
    // Google Workspace types
    if (mimeTypeLower.includes('google-apps.document')) return 'DOCUMENTO';
    if (mimeTypeLower.includes('google-apps.spreadsheet')) return 'FOGLIO';
    if (mimeTypeLower.includes('google-apps.presentation')) return 'SLIDE';
    if (mimeTypeLower.includes('google-apps.drawing')) return 'DISEGNO';
    if (mimeTypeLower.includes('google-apps.form')) return 'MODULO';
    if (mimeTypeLower.includes('google-apps.folder')) return 'CARTELLA';
    
    // Common file types
    if (mimeTypeLower.includes('pdf')) return 'PDF';
    if (mimeTypeLower.includes('image')) return 'IMMAGINE';
    if (mimeTypeLower.includes('video')) return 'VIDEO';
    if (mimeTypeLower.includes('audio')) return 'AUDIO';
    if (mimeTypeLower.includes('text')) return 'TESTO';
    if (mimeTypeLower.includes('word') || mimeTypeLower.includes('docx')) return 'WORD';
    if (mimeTypeLower.includes('excel') || mimeTypeLower.includes('xlsx')) return 'EXCEL';
    if (mimeTypeLower.includes('powerpoint') || mimeTypeLower.includes('pptx')) return 'POWERPOINT';
    
    // Fallback
    return mimeType.split('/')[1]?.toUpperCase() || 'FILE';
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Glass button with file icon and badge */}
      <button
        data-minimized-files-icon="true"
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 hover:bg-white/20 transition-all duration-200 text-black/70 hover:text-black"
        title={`${minimizedFiles.length} documento${minimizedFiles.length !== 1 ? 'i' : ''} aperto${minimizedFiles.length !== 1 ? 'i' : ''}`}
      >
        <FileText className="h-5 w-5" />
        
        {/* Badge with file count */}
        <span className="absolute -top-1 -right-1 h-5 w-5 bg-[#0f6cbd] text-white text-xs font-semibold rounded-full flex items-center justify-center">
          {minimizedFiles.length}
        </span>
      </button>
      
      {/* Dropdown with minimized files */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white/95 backdrop-blur-md border border-black/10 rounded-lg shadow-xl z-50 max-h-96 overflow-y-auto">
          <div className="p-3 border-b border-black/10">
            <h3 className="text-sm font-medium text-black">Documenti aperti</h3>
          </div>
          
          <div className="py-2">
            {minimizedFiles.map((minimizedFile) => (
              <div
                key={minimizedFile.id}
                className="flex items-center justify-between p-3 hover:bg-black/5 cursor-pointer"
                onClick={() => {
                  removeMinimizedFile(minimizedFile.id);
                  onMaximizeFile(minimizedFile.file);
                  setIsOpen(false);
                }}
              >
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  <FileText className="h-4 w-4 text-blue-500 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-black truncate">
                      {getFileName(minimizedFile.file.name)}
                    </p>
                    <p className="text-xs text-black/60">
                      {getFileTypeName(minimizedFile.file.mime_type)}
                    </p>
                  </div>
                </div>
                
                <button
                  onClick={(e) => handleClose(minimizedFile.id, minimizedFile.file.name, e)}
                  className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                  title="Chiudi"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}